import { Group, Option, useZakeke } from '@zakeke/zakeke-configurator-react';
import { T, useActualGroups, useUndoRedoActions, useUndoRegister } from 'Helpers';
import { useEffect, useState } from 'react';
import useStore from 'Store';
import styled from 'styled-components';
import savedCompositionsIcon from '../../assets/icons/saved_designs.svg';
import star from '../../assets/icons/star.svg';
import Designer from '../layout/Designer';
import DesignsDraftList from '../layout/DesignsDraftList';
import { Template, TemplatesContainer } from '../layout/SharedComponents';
import { MenuItem, MobileItemsContainer } from './MobileMenuComponents';
import TemplateGroup from 'components/TemplateGroup';
import { P } from 'Placeholder';

// Styled component for the container of the mobile menu
export const MobileMenuContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	width: 100%;
	position: relative;
	overflow: auto;
`;

// Styled component for the container of the price info text
const PriceInfoTextContainer = styled.div`
	font-size: 14px;
	padding: 0px 10px;
`;

// Helper to get all attributes from a group, flattened across steps
const getGroupAttributes = (group: Group) => {
	if (group.steps && group.steps.length > 0) {
		return group.steps.flatMap((step) => step.attributes);
	}
	return group.attributes;
};

// Helper to find which step an attribute belongs to
const findStepForAttribute = (group: Group, attributeId: number): number | null => {
	for (const step of group.steps) {
		if (step.attributes.some((a) => a.id === attributeId)) return step.id;
	}
	return null;
};

// MobileMenu component - restructured for two-row mobile layout:
// Top row: Option selection for the active group's active attribute
// Bottom row: Group navigation (step selection)
const MobileMenu = () => {
	const {
		isSceneLoading,
		templates,
		currentTemplate,
		setCamera,
		setTemplate,
		sellerSettings,
		selectOption,
		draftCompositions
	} = useZakeke();
	const {
		selectedGroupId,
		setSelectedGroupId,
		selectedAttributeId,
		setSelectedAttributeId,
		selectedStepId,
		setSelectedStepId,
		isUndo,
		isRedo,
		setSelectedTemplateGroupId,
		selectedTemplateGroupId,
		setLastSelectedItem
	} = useStore();

	const [optionsScroll, setOptionsScroll] = useState<number | null>(null);
	const [groupsScroll, setGroupsScroll] = useState<number | null>(null);
	const [isTemplateEditorOpened, setIsTemplateEditorOpened] = useState(false);
	const [isDesignsDraftListOpened, setisDesignsDraftListOpened] = useState(false);
	const [isTemplateGroupOpened, setIsTemplateGroupOpened] = useState(false);
	const [isStartRegistering, setIsStartRegistering] = useState(false);
	const undoRegistering = useUndoRegister();
	const undoRedoActions = useUndoRedoActions();

	// Include ALL groups (including stone groups, no filter)
	const actualGroups = useActualGroups() ?? [];

	const selectedGroup = selectedGroupId ? actualGroups.find((group) => group.id === selectedGroupId) : null;

	// Flatten all attributes across steps for the selected group
	const allAttributes = selectedGroup && selectedGroup.id > 0 ? getGroupAttributes(selectedGroup) : [];
	const allTemplateGroups =
		selectedGroup && selectedGroup.id > 0
			? selectedGroup.steps && selectedGroup.steps.length > 0
				? selectedGroup.steps.flatMap((step) => step.templateGroups)
				: selectedGroup.templateGroups
			: [];

	const selectedAttribute = allAttributes.find((attr) => attr.id === selectedAttributeId) ?? null;
	const selectedTemplateGroup =
		allTemplateGroups.find((tg) => tg.templateGroupID === selectedTemplateGroupId) ?? null;

	const options = selectedAttribute?.options ?? [];
	const groupIndex = actualGroups.findIndex((g) => g.id === selectedGroupId);

	// --- Handlers ---

	const handleGroupSelection = (groupId: number | null) => {
		setIsStartRegistering(undoRegistering.startRegistering());

		if (groupId && selectedGroupId !== groupId && !isUndo && !isRedo) {
			undoRedoActions.eraseRedoStack();
			undoRedoActions.fillUndoStack({ type: 'group', id: selectedGroupId, direction: 'undo' });
			undoRedoActions.fillUndoStack({ type: 'group', id: groupId, direction: 'redo' });
		}

		setSelectedGroupId(groupId);
		setOptionsScroll(0);
	};

	const handleStepSelection = (stepId: number | null) => {
		setIsStartRegistering(undoRegistering.startRegistering());

		if (selectedStepId !== stepId && !isUndo && !isRedo) {
			undoRedoActions.eraseRedoStack();
			undoRedoActions.fillUndoStack({ type: 'step', id: selectedStepId, direction: 'undo' });
			undoRedoActions.fillUndoStack({ type: 'step', id: stepId ?? null, direction: 'redo' });
		}

		setSelectedStepId(stepId);
		setOptionsScroll(0);
	};

	const handleAttributeSelection = (attributeId: number) => {
		setIsStartRegistering(undoRegistering.startRegistering());

		if (attributeId && selectedAttributeId !== attributeId && !isUndo && !isRedo) {
			undoRedoActions.eraseRedoStack();
			undoRedoActions.fillUndoStack({ type: 'attribute', id: selectedAttributeId, direction: 'undo' });
			undoRedoActions.fillUndoStack({ type: 'attribute', id: attributeId, direction: 'redo' });
		}

		setSelectedAttributeId(attributeId);
		setLastSelectedItem({ type: 'attribute', id: attributeId });
		setOptionsScroll(0);
	};

	const handleTemplateGroupSelection = (templateGroupId: number | null) => {
		setSelectedTemplateGroupId(templateGroupId);
		setLastSelectedItem({ type: 'template-group', id: templateGroupId });
		setIsTemplateGroupOpened(true);
	};

	const handleOptionSelection = (option: Option) => {
		const undo = undoRegistering.startRegistering();
		undoRedoActions.eraseRedoStack();
		undoRedoActions.fillUndoStack({
			type: 'option',
			id: options.find((opt) => opt.selected)?.id ?? null,
			direction: 'undo'
		});
		undoRedoActions.fillUndoStack({ type: 'option', id: option.id, direction: 'redo' });

		selectOption(option.id);
		undoRegistering.endRegistering(undo);

		try {
			if ((window as any).algho) (window as any).algho.sendUserStopForm(true);
		} catch (e) {}
	};

	// Option selection with auto-advance to next attribute or next group
	const handleOptionSelectionWithAdvance = (option: Option) => {
		handleOptionSelection(option);

		if (!selectedGroup || selectedGroup.id < 0 || !selectedAttribute) return;

		const currentAttrIndex = allAttributes.findIndex((a) => a.id === selectedAttributeId);

		if (currentAttrIndex >= 0 && currentAttrIndex < allAttributes.length - 1) {
			// Advance to next attribute within same group
			const nextAttr = allAttributes[currentAttrIndex + 1];
			const stepId = findStepForAttribute(selectedGroup, nextAttr.id);
			if (stepId !== null && stepId !== selectedStepId) handleStepSelection(stepId);
			handleAttributeSelection(nextAttr.id);
		} else {
			// Advance to next group
			if (groupIndex >= 0 && groupIndex < actualGroups.length - 1) {
				handleGroupSelection(actualGroups[groupIndex + 1].id);
			}
		}
	};

	const setTemplateByID = async (templateID: number) => await setTemplate(templateID);

	// Initial template selection
	useEffect(() => {
		if (templates.length > 0 && !currentTemplate) setTemplateByID(templates[0].id);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templates]);

	// Auto-select first group on mount
	useEffect(() => {
		if (actualGroups && actualGroups.length === 1 && actualGroups[0].id === -2) return;
		if (actualGroups && actualGroups.length > 0 && !selectedGroupId) {
			handleGroupSelection(actualGroups[0].id);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actualGroups, selectedGroupId]);

	// Auto-select first attribute when group changes (unless an attribute in this group is already selected, e.g. from mesh click)
	useEffect(() => {
		if (selectedGroup) {
			if (selectedGroup.id === -2) {
				setIsTemplateEditorOpened(true);
				return;
			}
			if (selectedGroup.id === -3) {
				setisDesignsDraftListOpened(true);
				return;
			}

			const attrs = getGroupAttributes(selectedGroup);

			// If the currently selected attribute already belongs to this group, keep it
			if (selectedAttributeId && attrs.some((a) => a.id === selectedAttributeId)) {
				return;
			}

			if (attrs.length > 0) {
				const stepId = findStepForAttribute(selectedGroup, attrs[0].id);
				if (stepId !== null) handleStepSelection(stepId);
				else handleStepSelection(null);
				handleAttributeSelection(attrs[0].id);
			} else {
				handleStepSelection(null);
				const tempGroups =
					selectedGroup.steps && selectedGroup.steps.length > 0
						? selectedGroup.steps.flatMap((s) => s.templateGroups)
						: selectedGroup.templateGroups;
				if (tempGroups.length > 0) {
					handleTemplateGroupSelection(tempGroups[0].templateGroupID);
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroup?.id]);

	// Camera
	useEffect(() => {
		if (!isSceneLoading && selectedGroup && selectedGroup.cameraLocationId) {
			setCamera(selectedGroup.cameraLocationId, false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroup?.id, isSceneLoading]);

	useEffect(() => {
		if (isStartRegistering) {
			undoRegistering.endRegistering(false);
			setIsStartRegistering(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isStartRegistering]);

	return (
		<MobileMenuContainer>
			{sellerSettings && sellerSettings.priceInfoText && (
				<PriceInfoTextContainer dangerouslySetInnerHTML={{ __html: sellerSettings.priceInfoText }} />
			)}

			{/* TOP ROW: Options for active attribute */}
			{selectedGroup && selectedGroup.id > 0 && selectedAttribute && (
				<MobileItemsContainer
					isLeftArrowVisible
					isRightArrowVisible
					scrollLeft={optionsScroll ?? 0}
					onScrollChange={(value) => setOptionsScroll(value)}
				>
					{selectedAttribute.options.map(
						(option) =>
							option.enabled && (
								<MenuItem
									isRound={selectedAttribute.optionShapeType === 2}
									description={T._d(option.description)}
									selected={option.selected}
									imageUrl={option.imageUrl ?? ''}
									label={T._d(P._(option.name))}
									hideLabel={selectedAttribute.hideOptionsLabel}
									largeIcon
									twoLineLabel
									key={option.guid}
									onClick={() => handleOptionSelectionWithAdvance(option)}
								/>
							)
					)}
				</MobileItemsContainer>
			)}

			{/* Template group content in top row */}
			{selectedGroup &&
				selectedGroup.id > 0 &&
				!selectedAttribute &&
				selectedTemplateGroup &&
				isTemplateGroupOpened && (
					<TemplateGroup
						key={selectedTemplateGroupId}
						templateGroup={selectedTemplateGroup!}
						isMobile
						onCloseClick={() => {
							setIsTemplateGroupOpened(false);
							handleTemplateGroupSelection(null);
						}}
					/>
				)}

			{/* Templates for Customize group */}
			{selectedGroup && selectedGroup.id === -2 && templates.length > 1 && (
				<TemplatesContainer>
					{templates.map((template) => (
						<Template
							key={template.id}
							selected={currentTemplate === template}
							onClick={async () => {
								await setTemplate(template.id);
							}}
						>
							{T._d(template.name)}
						</Template>
					))}
				</TemplatesContainer>
			)}

			{/* Designer / Customizer */}
			{selectedGroup?.id === -2 && isTemplateEditorOpened && (
				<Designer
					onCloseClick={() => {
						setIsTemplateEditorOpened(false);
						handleGroupSelection(null);
					}}
				/>
			)}

			{/* Saved Compositions */}
			{draftCompositions && selectedGroup?.id === -3 && isDesignsDraftListOpened && (
				<DesignsDraftList
					onCloseClick={() => {
						setisDesignsDraftListOpened(false);
						handleGroupSelection(null);
					}}
				/>
			)}

			{/* BOTTOM ROW: Group navigation (steps) */}
			<MobileItemsContainer
				isLeftArrowVisible
				isRightArrowVisible
				scrollLeft={groupsScroll ?? 0}
				onScrollChange={(value) => setGroupsScroll(value)}
				activeItemKey={selectedGroupId}
			>
				{actualGroups.map((group) => {
					if (group)
						return (
							<MenuItem
								key={group.guid || String(group.id)}
								selected={group.id === selectedGroupId}
								imageUrl={
									group.id === -3 ? savedCompositionsIcon : group.imageUrl ? group.imageUrl : star
								}
								label={group.name ? T._d(group.name) : T._('Customize', 'Composer')}
								onClick={() => handleGroupSelection(group.id)}
							/>
						);
					else return null;
				})}
			</MobileItemsContainer>
		</MobileMenuContainer>
	);
};

export default MobileMenu;
